import type { BucketSession } from './bucketReducer'
import type { Adventure, BucketMode, TasteId } from './bucket.types'

const MODE_ANSWERS: Readonly<Record<BucketMode, string>> = {
  self: 'This bucket is for me.',
  family: 'This bucket is for the family.',
  gift: 'This bucket is a gift.',
}

const ADVENTURE_ANSWERS: Readonly<Record<Adventure, string>> = {
  safe: 'Keep it familiar.',
  one_wildcard: 'Add one wildcard.',
  wild: 'Surprise me.',
}

const TASTE_LABELS: Readonly<Record<TasteId, string>> = {
  'freeze-dried': 'freeze-dried',
  fruity: 'fruity',
  sour: 'sour',
  chocolate: 'chocolate',
  caramel: 'caramel',
}

function joinLabels(labels: readonly string[]): string {
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}`
}

function tasteAnswer(session: BucketSession): string | null {
  const likes = session.preferences.likes.map((taste) => TASTE_LABELS[taste])
  const hardNos = session.preferences.hardNos.map(
    (taste) => TASTE_LABELS[taste],
  )
  const answers: string[] = []

  if (likes.length > 0) {
    answers.push(`I like ${joinLabels(likes)}`)
  }
  if (hardNos.length > 0) {
    answers.push(`Please leave out ${joinLabels(hardNos)}`)
  }

  return answers.length > 0 ? `${answers.join('. ')}.` : null
}

function formatTargetSpend(cents: number): string {
  const dollars = cents / 100
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

export function getLatestCustomerAnswer(
  session: BucketSession,
): string | null {
  switch (session.stage) {
    case 'eligibility':
      return session.eligibility.status === 'invalid' &&
        session.eligibility.postcode
        ? `My postcode entry was ${session.eligibility.postcode}.`
        : null

    case 'recipient':
      return session.eligibility.postcode
        ? `My postcode is ${session.eligibility.postcode}.`
        : null

    case 'preferences':
      return tasteAnswer(session) ??
        (session.mode ? MODE_ANSWERS[session.mode] : null)

    case 'adventure':
      return tasteAnswer(session)

    case 'budget':
      return ADVENTURE_ANSWERS[session.preferences.adventure]

    case 'revealed':
      return `Keep the bucket around ${formatTargetSpend(
        session.preferences.targetSpendCents,
      )}.`

    case 'surprise':
      return session.cadence === 'fortnightly'
        ? 'Make it a fortnightly surprise.'
        : 'Make it a weekly surprise.'

    case 'demo_complete':
      return session.cadence === 'weekly'
        ? 'My weekly Mystery Bucket is ready to review.'
        : session.cadence === 'fortnightly'
          ? 'My fortnightly Mystery Bucket is ready to review.'
          : 'I’ll take this bucket once.'
  }
}
